import React, { useState, useEffect, useCallback } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ShoppingCart, CreditCard, ShieldCheck, Share2 } from 'lucide-react';
import { CartItemRow } from './cart/CartItemRow';
import { CartShippingBadges } from './cart/CartShippingBadges';
import { CartTotals } from './cart/CartTotals';
import { cartUtils } from '@/data/translatedData';
import { toast } from 'sonner';
import Checkout from './Checkout';
import { useLanguage } from '@/context/LanguageContext';
import { tx } from '@/translations/translations';
import { encodeCartToUrl } from '@/utils/cartShare';

const SHIPPING_FEE = 50;
const FREE_SHIPPING_THRESHOLD = 5;

const CartSidebar = ({ isOpen, onClose }) => {
  const [cart, setCart] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const { language } = useLanguage();

  const updateCart = useCallback(() => {
    setCart(cartUtils.getCart());
  }, []);

  useEffect(() => {
    updateCart();
    window.addEventListener('cartUpdated', updateCart);
    
    return () => {
      window.removeEventListener('cartUpdated', updateCart);
    };
  }, [updateCart]);

  const getItemOptions = (item) => ({
    selectedTradition: item.selectedTradition,
    selectedHilos: item.selectedHilos,
    selectedCottonType: item.selectedCottonType,
    selectedColorValue: item.selectedColor?.value
  });

  const handleUpdateQuantity = (item, newQuantity) => {
    if (newQuantity < 1) return;
    cartUtils.updateQuantity(item.id, item.size, newQuantity, getItemOptions(item));
    updateCart();
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const handleRemove = (item) => {
    cartUtils.removeFromCart(item.id, item.size, getItemOptions(item));
    updateCart();
    window.dispatchEvent(new Event('cartUpdated'));
    toast.success(tx(language, 'Producto eliminado del carrito', 'Product removed from cart', 'המוצר הוסר מהסל'));
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Tu carrito está vacío');
      return;
    }
    setShowCheckout(true);
  };

  const handleCheckoutClose = () => {
    setShowCheckout(false);
    updateCart();
    // After a completed purchase the cart is empty — close the drawer too
    if (cartUtils.getCart().length === 0) onClose();
  };

  const handleCheckoutSuccess = () => {
    // Keep <Checkout> mounted (it lives inside SheetContent) so it can render
    // its OrderSuccessScreen with the "ya pagué" WhatsApp button.
    updateCart();
  };

  const handleShareCart = async () => {
    if (cart.length === 0) {
      toast.error(tx(language, 'Tu carrito está vacío', 'Your cart is empty', 'הסל שלך ריק'));
      return;
    }
    const shareUrl = encodeCartToUrl(cart);
    if (!shareUrl) return;
    const itemNames = cart.slice(0, 3).map(i => i.name).filter(Boolean).join(', ');
    const moreCount = cart.length > 3 ? tx(language, ` y ${cart.length - 3} más`, ` and ${cart.length - 3} more`, ` ועוד ${cart.length - 3}`) : '';
    const shareText = tx(language,
      `🛒 Mira mi carrito en Mitzva Fit: ${itemNames}${moreCount}\n\nÁbrelo aquí:`,
      `🛒 Check out my Mitzva Fit cart: ${itemNames}${moreCount}\n\nOpen here:`,
      `🛒 תראו את הסל שלי ב-Mitzva Fit: ${itemNames}${moreCount}\n\nפתחו כאן:`);

    // Try native Web Share API first (mobile-friendly)
    if (navigator.share) {
      try {
        await navigator.share({
          title: tx(language, 'Mi carrito de Mitzva Fit', 'My Mitzva Fit cart', 'הסל שלי ב-Mitzva Fit'),
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch (err) {
        // user cancelled or share failed → fall back to copy
        if (err?.name === 'AbortError') return;
      }
    }
    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      toast.success(tx(language, 'Link copiado al portapapeles ✓', 'Link copied to clipboard ✓', 'הקישור הועתק ✓'));
    } catch {
      toast.error(tx(language, 'No se pudo copiar el link', 'Could not copy link', 'לא ניתן להעתיק את הקישור'));
    }
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const qualifiesFreeShipping = itemCount >= FREE_SHIPPING_THRESHOLD;
  const shipping = qualifiesFreeShipping ? 0 : SHIPPING_FEE;
  const total = subtotal + shipping;
  const itemsToFreeShipping = Math.max(0, FREE_SHIPPING_THRESHOLD - itemCount);

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between gap-2 pr-8">
            <span className="flex items-center">
              <ShoppingCart className="h-5 w-5 mr-2" />
              {tx(language, 'Carrito de Compras', 'Shopping Cart', 'סל הקניות')}
            </span>
            {cart.length > 0 && (
              <button
                onClick={handleShareCart}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-cyan-600 hover:text-cyan-700 px-2 py-1 rounded-md hover:bg-cyan-50 transition"
                data-testid="share-cart-btn"
                title={tx(language, 'Compartir carrito', 'Share cart', 'שתף סל')}
              >
                <Share2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">
                  {tx(language, 'Compartir', 'Share', 'שתף')}
                </span>
              </button>
            )}
          </SheetTitle>
          <SheetDescription>
            {cart.length === 0
              ? tx(language, 'Tu carrito está vacío', 'Your cart is empty', 'הסל שלך ריק')
              : tx(language, `${cart.length} producto(s) en tu carrito`, `${cart.length} item(s) in your cart`, `${cart.length} מוצרים בסל שלך`)}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {cart.length === 0 ? (
            <div className="relative text-center py-10 px-6 rounded-2xl bg-gradient-to-br from-cyan-50 via-white to-slate-50 border border-slate-100 overflow-hidden">
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-cyan-500/10 rounded-full blur-2xl" />
              <div className="relative">
                <div className="mx-auto w-20 h-20 rounded-full bg-white shadow-sm ring-1 ring-slate-100 flex items-center justify-center mb-5">
                  <ShoppingCart className="h-9 w-9 text-cyan-500" strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-bold text-slate-800 mb-1">
                  {tx(language, 'Tu carrito está vacío', 'Your cart is empty', 'הסל שלך ריק')}
                </h3>
                <p className="text-xs text-slate-500 mb-6 leading-relaxed">
                  {tx(language, 'Descubre nuestros tzitziot con certificación Kosher Mehadrin', 'Discover our tzitziot with Kosher Mehadrin certification', 'גלו את הציציות שלנו עם כשרות מהדרין')}
                </p>
                <Button
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold shadow-sm"
                  onClick={onClose}
                  data-testid="continue-shopping-empty"
                >
                  {tx(language, 'Ver Productos', 'Browse Products', 'לצפייה במוצרים')}
                </Button>
                <div className="mt-5 pt-5 border-t border-slate-100 text-[11px] text-slate-400 flex items-center justify-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-green-500" />
                  {tx(language, '5 tzitziot = envío GRATIS', '5 tzitziot = FREE shipping', '5 ציציות = משלוח חינם')}
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Cart Items */}
              <div className="space-y-4">
                {cart.map((item, idx) => {
                  const uniqueKey = `${item.id}-${item.size}-${item.selectedTradition}-${item.selectedHilos}-${item.selectedColor?.value || ''}-${idx}`;
                  return (
                    <CartItemRow
                      key={uniqueKey}
                      item={item}
                      idx={idx}
                      language={language}
                      onUpdateQuantity={handleUpdateQuantity}
                      onRemove={handleRemove}
                    />
                  );
                })}
              </div>

              {/* Envío a Domicilio + Free Shipping badges */}
              <CartShippingBadges
                language={language}
                qualifiesFreeShipping={qualifiesFreeShipping}
                itemCount={itemCount}
                itemsToFreeShipping={itemsToFreeShipping}
                freeShippingThreshold={FREE_SHIPPING_THRESHOLD}
              />

              {/* Totals */}
              <CartTotals
                language={language}
                subtotal={subtotal}
                shipping={shipping}
                total={total}
                qualifiesFreeShipping={qualifiesFreeShipping}
              />

              {/* Security note */}
              <p className="text-[11px] text-slate-400 flex items-center justify-center gap-1">
                <ShieldCheck className="w-3 h-3 text-green-500" />
                {tx(language, 'Pagos seguros y encriptados', 'Secure and encrypted payments', 'תשלומים מאובטחים ומוצפנים')}
              </p>

              {/* Checkout Button */}
              <Button
                className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-base font-bold py-6 shadow-lg shadow-cyan-500/30 rounded-xl"
                onClick={handleCheckout}
                data-testid="checkout-btn"
              >
                <CreditCard className="h-5 w-5 mr-2" />
                {tx(language, 'Proceder al Pago', 'Proceed to Checkout', 'המשך לתשלום')}
              </Button>

              <Button
                variant="outline"
                className="w-full rounded-xl mt-3"
                onClick={onClose}
              >
                {tx(language, 'Continuar Comprando', 'Continue Shopping', 'המשך בקנייה')}
              </Button>
            </>
          )}
        </div>

        {/* PayPal Checkout Modal */}
        {showCheckout && (
          <Checkout 
            onClose={handleCheckoutClose}
            onSuccess={handleCheckoutSuccess}
          />
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSidebar;
